/**
 * Sales Agent Settings Hooks
 * The Settings tab's query and its save: one PUT with every part, sent by the tab's Save button.
 */

import { useAtom } from 'jotai';
import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';
import { salesAgentSettingsService } from '@/services/sales-agent-settings.service';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import { invalidateQueries } from '@/lib/query-client';
import type { UpdateSalesAgentSettingsRequestDto } from '@/modules/sales-agent/settings/types';

export const SALES_AGENT_SETTINGS_QUERY_KEY = 'sales-agent-settings';

const settingsQueryAtom = atomWithQuery((get) => ({
	// The brand is in the key as well as the request: a brand switch does not clear queries.
	queryKey: [SALES_AGENT_SETTINGS_QUERY_KEY, buScopeKey(get)],
	queryFn: () => salesAgentSettingsService.getSettings(buScopeParams(get)),
	staleTime: 30 * 1000
}));

const compareSourcesQueryAtom = atomWithQuery((get) => ({
	queryKey: [SALES_AGENT_SETTINGS_QUERY_KEY, 'compare-sources', buScopeKey(get)],
	queryFn: () => salesAgentSettingsService.getCompareSources(buScopeParams(get)),
	staleTime: 5 * 60 * 1000
}));

const saveSettingsMutationAtom = atomWithMutation((get) => ({
	mutationKey: [SALES_AGENT_SETTINGS_QUERY_KEY, 'save'],
	mutationFn: (patch: UpdateSalesAgentSettingsRequestDto) =>
		salesAgentSettingsService.saveSettings(patch, buScopeParams(get)),
	onSuccess: () => invalidateQueries([SALES_AGENT_SETTINGS_QUERY_KEY])
}));

export function useSalesAgentSettings() {
	const [query] = useAtom(settingsQueryAtom);
	return query;
}

/** The brand's product types and tags, for the Compare rows card. */
export function useCompareSources() {
	const [query] = useAtom(compareSourcesQueryAtom);
	return query;
}

export function useSaveSalesAgentSettings() {
	const [mutation] = useAtom(saveSettingsMutationAtom);
	return mutation;
}
